import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Eye, FileText, Heart, HelpCircle, Lock, ShieldCheck, Trash2, type LucideIcon } from '../components/icons';
import { GradientButton } from '../components/GradientButton';
import { BrandLogo } from '../components/BrandLogo';
import { TermsConsent } from '../components/TermsConsent';
import { SettingsRow } from '../components/SettingsRow';
import { acceptTerms } from '../services/profile';
import { VERSION_DOCUMENTS } from '../config/terms';
import { useAuth } from '../hooks/useAuth';
import { authErrorText } from '../utils/authErrors';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { useGabarit } from '../hooks/useGabarit';
import { horsFrancais, t } from '../i18n';
import { confirmer } from '../utils/confirmation';
import { fleche } from '../i18n/sens';
import { ouvrirLien } from '../utils/ouvrirLien';

const CONTACT_EMAIL = 'info@alternjob.com';

// Les trois piliers. Aucune couleur en dur : teinte vive + tint clair pris dans la charte.
// La colonne « sans toi » emploie corailSoft — la charte n'a pas de rose, et son trait est corail.
// Construit AU RENDU : constante de module = libellés figés à l'import.
const pillars = (): { icon: LucideIcon; label: string; vivid: string; soft: string }[] => [
  { icon: ShieldCheck, label: t('acceptTerms.pillars.data'), vivid: colors.violet, soft: colors.limeSoft },
  { icon: Eye, label: t('acceptTerms.pillars.transparency'), vivid: colors.electric, soft: colors.blueSoft },
  { icon: Heart, label: t('acceptTerms.pillars.consent'), vivid: colors.corail, soft: colors.corailSoft },
];

function PillarColumn({ icon: Icon, label, vivid, soft }: ReturnType<typeof pillars>[number]) {
  return (
    <View style={styles.pillar}>
      <View style={[styles.pillarCircle, { backgroundColor: soft }]}>
        <Icon size={22} color={vivid} strokeWidth={2} />
      </View>
      <Text style={styles.pillarLabel}>{label}</Text>
      <View style={[styles.pillarRule, { backgroundColor: vivid }]} />
    </View>
  );
}

// Ligne « document » : sa propre carte blanche, libellé en gras (le SettingsRow partagé est en
// graisse normale — on ne le modifie pas pour un seul écran).
function DocRow({ icon: Icon, label, onPress, testID }: { icon: LucideIcon; label: string; onPress: () => void; testID: string }) {
  return (
    <Pressable testID={testID} accessibilityRole="button" onPress={onPress} style={styles.docRow}>
      <View style={[styles.docIcon, { backgroundColor: colors.limeSoft }]}>
        <Icon size={18} color={colors.violet} strokeWidth={2} />
      </View>
      <Text style={styles.docLabel}>{label}</Text>
      <Text style={styles.chevron}>{fleche('›')}</Text>
    </Pressable>
  );
}

export interface AcceptTermsScreenProps {
  onAccepted: () => void; // consentement enregistré → on entre dans l'app
  onOpenTerms: () => void;
  onOpenPrivacy: () => void;
  onOpenHelp?: () => void;
  // true = re-sollicitation après révision du bundle (le compte existe déjà, avec des données).
  outdated?: boolean;
}

// Étape post-auth OBLIGATOIRE (pas de skip) : couvre notamment les inscriptions Google/Apple, dont
// le consentement n'a pas pu être posé au signup, ET la re-sollicitation après révision (§12).
export function AcceptTermsScreen({
  onAccepted,
  onOpenTerms,
  onOpenPrivacy,
  onOpenHelp,
  outdated = false,
}: AcceptTermsScreenProps) {
  const { large } = useGabarit();
  const { signOut, deleteAccount } = useAuth();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = async () => {
    if (!checked) return;
    setError(null);
    setSaving(true);
    try {
      await acceptTerms();
      onAccepted();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('acceptTerms.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Sortie sans accepter : on déconnecte, on ne détruit RIEN. Le compte et ses données restent
  // intacts et la personne est re-sollicitée à la prochaine connexion. Le gate étant bloquant
  // (les CGU interdisent de fournir le service sans acceptation), c'est la seule issue possible
  // hors suppression — d'où un libellé qui annonce la déconnexion au lieu de la laisser surprendre.
  const refuse = async () => {
    const result = await signOut();
    if (result.errorKey) setError(authErrorText(result.errorKey));
  };

  const confirmDelete = () => {
    confirmer(
      t('settings.deleteConfirmTitle'),
      t('settings.deleteConfirmBody'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: () => {
            void deleteAccount().then((r) => {
              if (r.errorKey) confirmer(t('settings.deleteFailedTitle'), authErrorText(r.errorKey));
            });
          },
        },
      ],
    );
  };

  const openHelp = () => {
    if (onOpenHelp) return onOpenHelp();
    ouvrirLien(`mailto:${CONTACT_EMAIL}`);
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={[styles.content, large && styles.contentL]} keyboardShouldPersistTaps="handled">
        <View style={styles.logoWrap}>
          <BrandLogo variant="wordmark" testID="accept-terms-logo" />
        </View>

        <Text style={styles.title}>
          {outdated ? t('acceptTerms.titleOutdated') : t('acceptTerms.titleFirst')}
        </Text>

        {outdated ? (
          // C'est CETTE phrase qui tient la « notification in-app » promise (§12 de la politique,
          // art. 13 des CGU) : elle nomme les DEUX documents révisés et y renvoie directement.
          <Text style={styles.subtitle} testID="accept-terms-changed">
            {t('acceptTerms.changedPrefix')}
            <Text style={styles.subtitleAccent} onPress={onOpenTerms} testID="accept-terms-changed-terms">
              {t('acceptTerms.changedTermsLink')}
            </Text>
            {t('acceptTerms.changedMiddle')}
            <Text style={styles.subtitleAccent} onPress={onOpenPrivacy} testID="accept-terms-changed-privacy">
              {t('acceptTerms.changedPrivacyLink')}
            </Text>
            {t('acceptTerms.changedSuffix', { version: VERSION_DOCUMENTS })}
          </Text>
        ) : (
          <Text style={styles.subtitle}>
            {t('acceptTerms.firstPrefix')}
            <Text style={styles.subtitleAccent}>{t('acceptTerms.firstAccent')}</Text>.
          </Text>
        )}

        <View style={styles.pillarCard} testID="accept-terms-pillars">
          {pillars().map((p) => (
            <PillarColumn key={p.label} {...p} />
          ))}
        </View>

        <View style={styles.docs}>
          <DocRow icon={FileText} label={t('acceptTerms.docTerms')} onPress={onOpenTerms} testID="accept-terms-doc-terms" />
          <DocRow icon={Lock} label={t('acceptTerms.docPrivacy')} onPress={onOpenPrivacy} testID="accept-terms-doc-privacy" />
          {horsFrancais() ? (
            <Text style={styles.documentsFr} testID="accept-terms-docs-fr">
              {t('legalHeaders.documentsEnFrancais')}
            </Text>
          ) : null}
        </View>

        <TermsConsent
          testID="accept-terms-consent"
          checked={checked}
          onToggle={setChecked}
          onOpenTerms={onOpenTerms}
          onOpenPrivacy={onOpenPrivacy}
          linkColor={colors.violet}
        />

        {error ? (
          <Text testID="accept-terms-error" style={styles.error}>
            {error}
          </Text>
        ) : null}

        <GradientButton
          label={t('acceptTerms.accept')}
          onPress={accept}
          loading={saving}
          disabled={!checked}
          withArrow
          testID="accept-terms-submit"
        />

        <Pressable accessibilityRole="button" onPress={() => void refuse()} testID="accept-terms-refuse">
          <Text style={styles.laterLink}>
            {outdated ? t('acceptTerms.refuseOutdated') : t('acceptTerms.refuseFirst')}
          </Text>
        </Pressable>

        <View style={styles.footer}>
          <SettingsRow
            label={t('acceptTerms.help')}
            icon={HelpCircle}
            accent="blue"
            onPress={openHelp}
            testID="accept-terms-help"
          />
          {/* Suppression réservée à la RE-SOLLICITATION. Refuser des CGU révisées ne doit pas
              enfermer : le gate est bloquant, donc sans cette issue il faudrait ACCEPTER pour
              atteindre les Réglages et exercer son droit à l'effacement (RGPD art. 17), et Apple
              exige une voie de suppression accessible. À la première acceptation le compte vient
              d'être créé et ne porte rien : la déconnexion suffit, et une action destructive n'a
              pas sa place sur un écran subi à chaque lancement. */}
          {outdated ? (
            <>
              <View style={styles.divider} />
              <SettingsRow
                label={t('acceptTerms.deleteAccount')}
                icon={Trash2}
                accent="lime"
                onPress={confirmDelete}
                testID="accept-terms-delete"
              />
            </>
          ) : null}
        </View>

        {outdated ? (
          <Text style={styles.rights}>
            Une question sur ces changements ? Écris-nous à{' '}
            <Text
              style={styles.rightsLink}
              onPress={() => ouvrirLien(`mailto:${CONTACT_EMAIL}`)}
              testID="accept-terms-contact"
            >
              {CONTACT_EMAIL}
            </Text>
            .
          </Text>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  // Blanc comme AuthScreen et OnboardingScreen : le gate appartient au même parcours. Sur grey50,
  // le fond opaque cuit dans le PNG du wordmark ressortait en rectangle. Les cartes ci-dessous se
  // délimitent donc au trait grey200 et non plus par contraste de fond.
  container: { flex: 1, backgroundColor: colors.white },
  content: { padding: screenPadding, gap: spacing.lg, paddingBottom: spacing.xxl },
  // Écran large : colonne de lecture centrée, pas une ligne de texte de 1200 px.
  contentL: { width: '100%', maxWidth: 980, alignSelf: 'center', padding: spacing.xl, paddingBottom: spacing.xxl },
  logoWrap: { alignItems: 'center', marginTop: spacing.xl },

  title: {
    fontSize: typography.display.size,
    fontWeight: typography.display.weight,
    lineHeight: typography.display.lineHeight,
    color: colors.night,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey500,
    textAlign: 'center',
  },
  subtitleAccent: { color: colors.violet, fontWeight: '700' },

  // ── Carte des trois piliers ──
  pillarCard: {
    flexDirection: 'row',
    backgroundColor: colors.white,
    borderRadius: radius.lg,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  pillar: { flex: 1, alignItems: 'center', gap: spacing.sm },
  pillarCircle: {
    width: 48,
    height: 48,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillarLabel: {
    fontSize: typography.small.size,
    lineHeight: typography.small.lineHeight,
    fontWeight: '600',
    color: colors.grey900,
    textAlign: 'center',
  },
  pillarRule: { width: 24, height: 3, borderRadius: radius.pill },

  // ── Lignes documents ──
  docs: { gap: spacing.sm },
  documentsFr: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  docRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    backgroundColor: colors.white,
    borderRadius: radius.lg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  docIcon: {
    width: 34,
    height: 34,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  docLabel: { flex: 1, fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.grey900 },
  chevron: { fontSize: typography.h3.size, color: colors.grey400 },

  error: { color: colors.error, fontSize: typography.small.size, textAlign: 'center' },

  laterLink: {
    fontSize: typography.body.size,
    fontWeight: '700',
    color: colors.violet,
    textAlign: 'center',
  },

  // ── Pied d'écran ──
  footer: {
    backgroundColor: colors.white,
    borderRadius: radius.lg,
    paddingHorizontal: spacing.lg,
    marginTop: spacing.sm,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  divider: { height: 1, backgroundColor: colors.grey200 },

  rights: {
    fontSize: typography.small.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey500,
    textAlign: 'center',
  },
  rightsLink: { color: colors.violet, fontWeight: '700' },
});
